import { ColDef, GetRowIdFunc, GridOptions, SideBarDef } from 'ag-grid-community';

/** Sizing shared by the grids that use the `tk-grid` theme (see tk-grid.scss). */
export const TK_GRID_HEADER_HEIGHT = 48;
export const TK_GRID_ROW_HEIGHT = 68;

/** How long a filter input waits after the last keystroke before the grid filters. */
export const TK_GRID_FILTER_DEBOUNCE_MS = 500;

const TK_DEFAULT_COL_DEF: ColDef = {
  resizable: true,
  floatingFilter: true,
  // No column menu (three dots) and no filter buttons: the header clear button (TkGridDirective) replaces them.
  suppressHeaderMenuButton: true,
  suppressHeaderFilterButton: true,
  suppressFloatingFilterButton: true,
  filterParams: { debounceMs: TK_GRID_FILTER_DEBOUNCE_MS },
};

/**
 * The column defaults every `tk-grid` shares, with the page's own on top. `filterParams` are merged, so a page
 * that sets its own keeps the shared debounce unless it sets `debounceMs` itself.
 */
export function tkColDef(extra: ColDef = {}): ColDef {
  return {
    ...TK_DEFAULT_COL_DEF,
    ...extra,
    filterParams: { ...TK_DEFAULT_COL_DEF.filterParams, ...extra.filterParams },
  };
}

/**
 * Opt a column back into the floating-filter type button (Equals / In range, etc.).
 * Hidden by default via `tkColDef` (`suppressFloatingFilterButton: true`). Spread onto the ColDef that needs it.
 */
export function tkFloatingFilterButton(): Pick<ColDef, 'suppressFloatingFilterButton' | 'floatingFilterComponentParams'> {
  return {
    suppressFloatingFilterButton: false,
    floatingFilterComponentParams: { suppressFilterButton: false },
  };
}

/** The Columns and Filters tool panels, closed until opened. Only the panel shown on first open differs by page. */
export function tkSideBar(defaultToolPanel?: 'columns' | 'filters'): SideBarDef {
  return {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel,
    hiddenByDefault: true,
  };
}

export interface TkGridOptionsConfig {
  /** `serverSide` and `infinite` fetch one page at a time; `clientSide` holds every row in the browser. */
  rowModelType: 'serverSide' | 'infinite' | 'clientSide';
  pageSize: number;
  pageSizeOptions: number[];
  /** Must return the same id for the same row every time, or a block-based datasource reloads the block. */
  getRowId: GetRowIdFunc;
  sideBar: SideBarDef;
}

/** Heights, pagination, side bar and, for block-based row models, the block cache shared by every `tk-grid`. The page adds the rest. */
export function tkGridOptions(config: TkGridOptionsConfig): GridOptions {
  const options: GridOptions = {
    rowModelType: config.rowModelType,
    headerHeight: TK_GRID_HEADER_HEIGHT,
    rowHeight: TK_GRID_ROW_HEIGHT,
    pagination: true,
    paginationPageSize: config.pageSize,
    paginationPageSizeSelector: config.pageSizeOptions,
    getRowId: config.getRowId,
    sideBar: config.sideBar,
  };
  if (config.rowModelType !== 'clientSide') {
    // AG Grid warns when these are set on a client-side grid.
    options.cacheBlockSize = config.pageSize;
    options.maxBlocksInCache = 5;
  }
  return options;
}
